import { useIntl } from 'react-intl';
import PropTypes from '../../../../../../../utils/propTypes';
import { connect } from 'react-redux';
import * as Actions from '../../../../../../../services/redux/deliveryNotes/actions';
import {
  Link,
  useNavigate,
  useOutletContext,
  useSearchParams,
} from 'react-router-dom';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Flex, Space } from 'antd';
import { NotificationsContext } from '../../../../../../../context/NotificationsContext';
import {
  downloadXlsx,
  getPlants,
} from '../../../../../../../services/api/deliveryNote';
import Spinner from '../../../../../../../components/Spinner';
import styles from './index.module.scss';
import AntdSelect from '../../../../../../../components/AntdSelect';
import AccionaButton from '../../../../../../../components/AccionaButton';
import AccionaTable from '../../../../../../../components/AccionaTable';
import { COLUMNS_HEADERS } from './columns';
import AccionaTableButton from '../../../../../../../components/AccionaTable/AccionaTableButton';
import MashControlBadge from '../MashControlBadge';
import CertifiedDispersionBadge from '../CertifiedDispersionBadge';
import ConcreteQualityBadge from '../ConcreteQualityBadge';
import BatchCompliance from '../BatchCompliance';
import useNormativeApi from '../EditModal/hooks/useNormativeApi';
import useLotification from '../EditModal/hooks/useLotification';
import { downloadPDF } from '../../../../../../../services/api/normative';
import { useCallback } from 'react';
import AccionaConfirm from '../../../../../../../components/AccionaConfirm';

const { actionsWrapper, link } = styles;

const TraceabilityTable = ({
  getDeliveryNotes,
  normative,
  cleanReducer,
  onViewClick,
  onEditClick,
  structuralElements,
}) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { project } = useOutletContext();
  const [plants, setPlants] = useState();
  const params = useRef();
  const [selectedPlant, setSelectedPlant] = useState(plants?.[0]);
  const [downloadingPDF, setDownloadingPDF] = useState(false);
  const [downloadingXlsx, setDownloadingXlsx] = useState(false);
  const { pushNotification } = useContext(NotificationsContext);
  const { getProjectStatus } = useNormativeApi();
  const { unBatchItem } = useLotification();
  const [searchParams] = useSearchParams();
  const did = searchParams.get('did');
  const plant = searchParams.get('plant');
  const [didFilter, setDidFilter] = useState(did);
  const [showConfirmModal, setShowConfirmModal] = useState();
  const columns = useMemo(
    () =>
      COLUMNS_HEADERS({
        structuralElements,
        normative,
      }),
    [structuralElements, normative],
  );

  const initialFilters = useMemo(() => {
    return {
      sortable: 'arriving_date-ASC',
      plant: selectedPlant,
      docket_number: didFilter || undefined,
    };
  }, [didFilter, selectedPlant]);

  const actions = useCallback(
    (item) => {
      return (
        <>
          <AccionaTableButton
            type="view"
            toolTip={intl.formatMessage({
              id: 'viewAction.traceability',
            })}
            onClick={() => {
              onViewClick(item);
            }}
          />
          <AccionaTableButton
            type="edit"
            toolTip={intl.formatMessage({
              id: 'editAction.traceability',
            })}
            onClick={() => {
              onEditClick(item);
            }}
          />
          {item.rawData.concrete_batch && (
            <AccionaTableButton
              type="unBatch"
              toolTip={intl.formatMessage({
                id: 'unBatchAction.traceability',
              })}
              onClick={() => {
                setShowConfirmModal({
                  message: intl.formatMessage({
                    id: 'pages.quality.normative.traceability.list.unbatch.confirm.message',
                  }),
                  onConfirm: () => {
                    setShowConfirmModal();
                    const payload = {
                      deliveryNote: item?.rawData,
                      formValues: {
                        batch_code: item?.rawData.concrete_batch_data?.id,
                      },
                    };
                    getProjectStatus(payload, true).then(() => {
                      unBatchItem(
                        payload.deliveryNote,
                        payload.formValues,
                        true,
                      )
                        .then(() => {
                          pushNotification({
                            message: intl.formatMessage({
                              id: 'pages.quality.normative.traceability.edit.unbatch.notification.success',
                            }),
                            type: 'success',
                          });
                        })
                        .catch(() => {
                          pushNotification({
                            message: intl.formatMessage({
                              id: 'pages.quality.normative.traceability.edit.unbatch.notification.error',
                            }),
                            type: 'error',
                          });
                        });
                    });
                  },
                  onCancel: () => {
                    setShowConfirmModal();
                  },
                });
              }}
            />
          )}
        </>
      );
    },
    [
      getProjectStatus,
      intl,
      onEditClick,
      onViewClick,
      pushNotification,
      unBatchItem,
    ],
  );

  useEffect(() => {
    params.current = {
      ...(params.current || {}),
      filters: initialFilters,
    };
  }, [initialFilters]);

  const data = useCallback(
    (data) => {
      return data.map((item) => {
        const concreteBatch = item.concrete_batch_data;

        return {
          ...item,
          rawData: item,
          control_type: intl.formatMessage({
            id:
              item.control_type === 'Estadístico'
                ? 'control_type.statistic'
                : item.control_type === '100%'
                ? 'control_type.100%'
                : '---',
          }),
          concrete_batch: !concreteBatch?.batch_code ? (
            '---'
          ) : (
            <Space>
              <strong>{`${concreteBatch?.batch_code}`}</strong>
              <small
                className={`${
                  !concreteBatch?.batch_volume_compliance ? 'text-danger' : ''
                }`}
              >
                {` (${concreteBatch?.batch_volume?.toFixed(2)}`}
                {` m`}
                <sup>3</sup>)
              </small>
              <BatchCompliance concreteBatch={concreteBatch} />
            </Space>
          ),
          docket_number: (
            <Flex justify="flex-start" align="center" gap={5}>
              <Link
                to={`../../ocr/scannedDocuments/${item.id}`}
                className={link}
              >
                {item.docket_number}
              </Link>
              {item.mash_control && <MashControlBadge />}
              {item.certified_dis && normative === 'coe' && (
                <CertifiedDispersionBadge />
              )}
              {item.concrete_quality && (
                <ConcreteQualityBadge normative={normative} />
              )}
            </Flex>
          ),
          quantity: concreteBatch?.batch_volume ? (
            <span
              className={`${
                !concreteBatch?.batch_volume_compliance ? 'text-danger' : ''
              }`}
            >
              {` ${concreteBatch?.batch_volume?.toFixed(2)}`}
              {` m`}
              <sup>3</sup>
            </span>
          ) : (
            '---'
          ),
          delivered: (
            <>
              {`${item.delivered} m`} <sup>3</sup>
            </>
          ),
          structural_element: item.structural_element || '--',
          elements: item.elements,
          concreteBatch: concreteBatch?.batch_code,
          laboratoryNoteData: item.laboratory_note_data,
          batchCompliance: concreteBatch ? (
            <BatchCompliance concreteBatch={concreteBatch} />
          ) : null,
        };
      });
    },
    [intl, normative],
  );

  useEffect(() => {
    const getAllPlants = async () => {
      const { data } = await getPlants(project?.id_project);
      setPlants(data.plants);
      setSelectedPlant(plant || data.plants[0]);
    };
    if (project?.id_project) {
      getAllPlants();
    }
  }, [plant, project?.id_project]);

  useEffect(() => {
    return () => {
      cleanReducer();
    };
  }, [cleanReducer]);

  if (!selectedPlant) {
    return <Spinner />;
  }
  return (
    <>
      <div className={`row ${actionsWrapper}`}>
        <div className="col-sm-12">
          <Flex wrap={false} justify="space-between" align="center" gap={10}>
            <Flex justify="flex-start" gap={10}>
              <AntdSelect
                options={plants?.map((plant) => ({
                  label: plant,
                  value: plant,
                }))}
                style={{ width: 300, height: 52 }}
                size="large"
                placeholder={intl.formatMessage({
                  id: 'pages.quality.normative.traceability.select.placeholder',
                })}
                theme="white"
                value={selectedPlant}
                onChange={(plant) => {
                  setSelectedPlant(plant);
                  params.current = {
                    ...params.current,
                    filters: {
                      sortable: 'arriving_date-ASC',
                      plant: plant,
                    },
                  };
                  setDidFilter(undefined);
                }}
              />
              <AccionaButton
                id="export-traceability-pdf"
                type="white"
                msg="pdf"
                loading={downloadingPDF}
                onClick={() => {
                  setDownloadingPDF(true);
                  downloadPDF(project.id_project)
                    .then(() => {
                      setDownloadingPDF(false);
                      pushNotification({
                        message: intl.formatMessage({
                          id: 'pages.quality.normative.traceability.export.success',
                        }),
                        type: 'success',
                      });
                    })
                    .catch((error) => {
                      setDownloadingPDF(false);
                      pushNotification({
                        message: intl.formatMessage({
                          id: 'pages.quality.normative.traceability.export.error',
                        }),
                        type: 'error',
                      });
                    });
                }}
                classIcon="icon fas fa-download"
              />
              <AccionaButton
                id="export-traceability-xls"
                type="white"
                msg="xlsx"
                loading={downloadingXlsx}
                onClick={() => {
                  setDownloadingXlsx(true);
                  downloadXlsx({
                    ...params.current,
                    workcode: project?.work_code,
                  })
                    .then(() => {
                      setDownloadingXlsx(false);
                      pushNotification({
                        message: intl.formatMessage({
                          id: 'pages.quality.normative.traceability.export.success',
                        }),
                        type: 'success',
                      });
                    })
                    .catch(() => {
                      setDownloadingXlsx(false);
                      pushNotification({
                        message: intl.formatMessage({
                          id: 'pages.quality.normative.traceability.export.error',
                        }),
                        type: 'error',
                      });
                    });
                }}
                classIcon="icon fas fa-download"
              />
            </Flex>
            <Flex>
              <AccionaButton
                id="statisticalControl"
                type="primary"
                onClick={() => {
                  //navigate('../statistical-control');
                  navigate(
                    `../../${
                      normative === 'ehe' ? normative : 'ce'
                    }/concreteTypesEst`,
                  );
                }}
                msg={intl.formatMessage({ id: 'button.statisticalControl' })}
                classIcon="icon fas fa-chart-bar"
              />
              <i className="fa-solid fa-chart-simple"></i>
              <AccionaButton
                id="100%Control"
                type="primary"
                onClick={() => {
                  //navigate('../100%-control');
                  navigate(
                    `../../${
                      normative === 'ehe' ? normative : 'ce'
                    }/concreteTypes100`,
                  );
                }}
                msg={intl.formatMessage({ id: 'button.100%Control' })}
                classIcon="icon fas fa-percentage"
              />
            </Flex>
          </Flex>
        </div>
      </div>
      <div className="row">
        <div className="col-sm-12">
          <AccionaTable
            allowClear={
              !!params.current?.filters &&
              Object.entries(params.current?.filters).filter(
                ([key, value]) =>
                  !['plant', 'pid', 'sortable'].includes(key) && value,
              ).length > 0
            }
            onClearFilters={() => {
              params.current = {
                ...params.current,
                filters: {
                  sortable: 'arriving_date-ASC',
                  plant: selectedPlant,
                },
              };
              setDidFilter(undefined);
            }}
            onChange={({
              filters,
              pagination: { current, total, pageSize },
            }) => {
              params.current = {
                filters: { ...filters, pid: project?.id_project },
                size: pageSize,
                page: current,
              };
            }}
            rowHeight="40px"
            title={intl.formatMessage({
              id: 'pages.quality.normative.traceability.list.title',
            })}
            pageSize={10}
            initialFilters={initialFilters}
            columns={columns}
            entity="deliveryNotes"
            getItems={getDeliveryNotes}
            actions={actions}
            dataParseFunction={data}
          />
        </div>
      </div>
      {showConfirmModal && (
        <AccionaConfirm
          message={showConfirmModal.message}
          onCancel={showConfirmModal.onCancel}
          onConfirm={showConfirmModal.onConfirm}
        />
      )}
    </>
  );
};

TraceabilityTable.propTypes = {
  getDeliveryNotes: PropTypes.func.isRequired,
  cleanReducer: PropTypes.func.isRequired,
  normative: PropTypes.oneOf(['coe', 'ehe']).isRequired,
  onViewClick: PropTypes.func.isRequired,
  onEditClick: PropTypes.func.isRequired,
  structuralElements: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      structural_element: PropTypes.string,
    }),
  ),
};

TraceabilityTable.defaultProps = {
  structuralElements: [],
};

export default connect(() => ({}), {
  getDeliveryNotes: Actions.getDeliveryNotes,
  cleanReducer: Actions.initReducer,
})(TraceabilityTable);
